import { Checklist, ExpandMore, ManageSearch } from "@mui/icons-material";
import { Card, Alert, Accordion, AccordionSummary, Typography, AccordionDetails, LinearProgress, Chip, TablePagination, Box } from "@mui/material";
import axios, { AxiosResponse } from "axios";
import { format, formatDate } from "date-fns";
import { useState, useContext, useEffect } from "react";
import { Updater } from "use-immer";
import { API_ENDPOINT } from "../../../../../configurations/global.config";
import { useSnackBar } from "../../../../../contexts/snackbar";
import { AuthContext } from "../../../../../contexts/user-context/user-context";
import { OrderDeliveryTaskState } from "../../../../../enums/tasks";
import { DriverType } from "../../../../../interfaces/delivery-company/drivers";
import { IOrderDeliveryTask, IPickupTask } from "../../../../../interfaces/delivery-company/tasks";
import { get_cod } from "../../../../../utils/cod";
import { displayLocation } from "../../../../../utils/locations";
import { displayMoney, Money } from "../../../../../utils/money";
import UpdateCommissionDialog from "../../../../common/delivery-task-commission/update-commission-dialog";
import DoubleDateOrdersFilter from "../../../../common/double-date-orders-filter/double-date-orders-filter";
import { generateDateFilterQueryParam } from "../../../../common/double-date-orders-filter/utils";
import { DoubleDateWithMode, DoubleDateMode } from "../../../../common/double-date-picker/double-date-picker.interfaces";
import { ErrorsFlattenner } from "../../../../common/errors-flattenner/errors-flattenner";
import FilterBar from "../../../../common/filter-bar/filter-bar";
import { buildQueryParams } from "../../../../common/filter-bar/filter-bar.utils";
import { IFilterOption } from "../../../../common/filter-bar/types";
import GenericTable from "../../../../common/generic-table/generic-table";
import IncompleteAddressError from "../../../../common/incomplete-address-error/incomplete-address-error";
import OrderNotes from "../../../../common/order-notes/order-notes";
import { showWorkflow } from "../../../../common/styles/workflow-display";
import DriverAmountsSummary from "../driver-amounts-summary";
import { Workflow } from "../../../../../enums/orders";
import { MerchantWithIcon } from "../../../../common/styles/merchant-with-icon";
import { CustomerWithIcon } from "../../../../common/styles/customer-with-icon";

interface InvoiceablePickupTasksProps {
  driver: DriverType;
  selectedItems: Map<string, IPickupTask>;
  updateSelectedItems: Updater<Map<string, IPickupTask>>;
  invoicePurpose: 'NET' | 'COLLECTION' | 'COMMISSION';
}

function InvoiceablePickupTasks({
  driver,
  selectedItems,
  updateSelectedItems,
  invoicePurpose
}: InvoiceablePickupTasksProps) {
  const [itemsList, setItemsList] = useState<IPickupTask[]>();
  const [itemsCount, setIemsCount] = useState<number>(0);
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {showMessage} = useSnackBar()
  const [updateCommissionFor, setUpdateCommissionFor] = useState<IPickupTask | null>(null);

  console.log("tasks = ", itemsList)

  const [dateFilter, setDateFilter] = useState<DoubleDateWithMode>({
    startDate: null,
    endDate: null,
    mode: DoubleDateMode.DELIVERY_DATE,
  });

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 1000,
  });

  const totalSelected = selectedItems.size;

  const { user, config } = useContext(AuthContext);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };
  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const resetSelectionState = () => {
    updateSelectedItems((selectedItems) => {
      selectedItems.clear();
    });
  };

  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
    resetSelectionState();
  };

  const dateFilterAction = (dateFilterValue: DoubleDateWithMode) => {
    resetPaginationControls();
    setDateFilter(dateFilterValue);
  };

  const resetAll = () => {
    resetPaginationControls();
    resetSelectionState();
  };

  // Fetching delivery tasks.
  useEffect(() => {
    let response: any;
    const relevant_fields = [
      'id',
      'status',
      'driver',
      'driver_commission',
      'package_count',
      'creation_date',
      'workflow',
      'merchant_location',
      'customer_location',
    ];

    const getData = async () => {
      let endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}&driver=${driver.id}&invoiceable_with_purpose=${invoicePurpose}`;
      
      resetSelectionState()
      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;
      endpoint += `&${generateDateFilterQueryParam(dateFilter)}`;

      try {
        response = await axios.get<{ count: number; results: IOrderDeliveryTask[] }>(
          endpoint,
          config
        );
          setItemsList(response.data.results);
          setIemsCount(response.data.count);
      } catch (error) {
        showMessage("Failed to load orders", 'error')
      }
      setIsLoading(false);
    };
    setIsLoading(true);
    getData();
    console.log(response);
  }, [dateFilter, filters, JSON.stringify(controller), invoicePurpose]);


  return (
    <div>
      <Card elevation={0}>
        {message !== '' && (
          <>
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>

            <br />
          </>
        )}

        <Accordion elevation={0}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography
              variant="h5"
              component="h5"
              sx={{ textAlign: 'center',  mb: 1 }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <ManageSearch fontSize="large" color="primary" />
                <span>
                  <b>Search and Filter Controls</b>
                </span>
              </div>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DoubleDateOrdersFilter dateFilterAction={dateFilterAction} />
            <br />


            <FilterBar
                filterQueryAction={filterQueryAction}
                currentlySelectedFilters={filters}
                filterKeys={["customer", "merchant", "workflow", "district", "governorate"]}
            />
        
          </AccordionDetails>
        </Accordion>

        {isLoading ? (
          <LinearProgress />
        ) : (
          <>
            {itemsList ? (
              <>
              <Box display={"flex"} width={"100%"} justifyContent={"space-between"} alignItems={"center"} sx={{mb:2}}>

                 <Chip
                  icon={<Checklist />}
                  color="primary"
                  label={`Selected ${totalSelected}/${itemsCount}`}
                  variant={
                    totalSelected === itemsCount && totalSelected > 0
                      ? 'filled'
                      : 'outlined'
                  }
                />
                
                {/* {selectedItems.size > 0 && <DriverAmountsSummary pic={Array.from(selectedItems.values())}  />} */}

              </Box>
               
                <GenericTable<IPickupTask>
                  sourceView="create-new-driver-delivery-invoice"
                  items={itemsList}
                  selectedItems={selectedItems}
                  updateSelectedItems={updateSelectedItems}
                  isLoading={isLoading}
                  fullWidth
                  selectionEnabled
                  columns={[
                    {
                      id: 'task_id',
                      header: <p>Task ID</p>,
                      display_body: (task: IPickupTask) => (
                        <b>
                          <u>{task.id}</u>
                        </b>
                      ),
                    },
                    {
                      id: 'creation_date',
                      header: <p>Created At</p>,
                      display_body: (task: IPickupTask) => (
                        <p>
                          {format(
                            new Date(task.creation_date ?? ''),
                            'dd-MM-yyyy'
                          )}
                        </p>
                      ),
                    },
                    {
                      id: 'workflow',
                      header: <p>Workflow</p>,
                      display_body: (task: IPickupTask) => (
                        <p>{showWorkflow(task.workflow, false,undefined, "")}</p>
                      ),
                    },
                    {
                      id: 'Pickup From',
                      header: <p>Pickup From</p>,
                      display_body: (task: IPickupTask) => {
                        if (task.workflow === Workflow.STANDARD)
                          return <MerchantWithIcon merchant={task.merchant} />
                        else
                          return <CustomerWithIcon customer={task.customer} />
                      },
                    },
                    {
                      id: 'package count',
                      header: <p># Packages</p>,
                      display_body: (task: IPickupTask) => (
                        <p>{task.package_count}</p>
                      ),
                    },
                    {
                      id: 'task_state',
                      header: <p>Status</p>,
                      display_body: (task: IPickupTask) => (
                        <>{task.status} </>
                      ),
                    },
                  ]}
                  getItemUID={(t) => t.id}
                />

                <TablePagination
                  component="div"
                  onPageChange={handlePageChange}
                  page={controller.page}
                  count={itemsCount}
                  rowsPerPage={controller.rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
                  labelRowsPerPage=""
                  style={{ overflow: 'visible' }}
                />
                {/* {updateCommissionFor && (
                    <UpdateCommissionDialog 
                    // should update commission as a failing task
                    task={updateCommissionFor!} 
                    updateDeliveryTaskCommission={function (m: Money[]): void {
                        const newTask = {
                            ...updateCommissionFor,
                            driver_commission: updateCommissionFor.status == OrderDeliveryTaskState.FAILED ? updateCommissionFor.driver_commission : m,
                            failed_delivery_commission: updateCommissionFor.status == OrderDeliveryTaskState.FAILED ? m : updateCommissionFor.driver_commission,
                        }
                        setItemsList(itemsList.map(item => item.id != updateCommissionFor.id ? item : newTask))
                        
                        const newSelectedItems = new Map(selectedItems)
                        newSelectedItems.set(updateCommissionFor.id,  newTask)
                        updateSelectedItems(newSelectedItems)
                    } } 
                    onClose={()=>setUpdateCommissionFor(null)} />
                
                )} */}
              </>
            ) : (
              <Alert severity="error">Failed to load orders</Alert>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default InvoiceablePickupTasks;
